import { motion } from "framer-motion";
import { Phone, Mail, MapPin, Clock, MessageCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";

export default function ContactPage() {
  return (
    <div data-testid="contact-page">
      {/* Hero */}
      <section className="bg-gradient-to-br from-slate-50 to-white py-16 md:py-24">
        <div className="container-width text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6">
              Contact Us
            </h1>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Get in touch with Dr. Sanjay Sachdeva's clinic for appointments, enquiries, or any questions.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Info */}
      <section className="section-padding bg-slate-50">
        <div className="container-width">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
            {/* Phone */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-white rounded-xl p-8 shadow-sm"
            >
              <div className="w-12 h-12 rounded-xl bg-sky-50 flex items-center justify-center mb-4">
                <Phone className="w-6 h-6 text-sky-600" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-4">Phone</h3>
              <div className="space-y-2">
                <a href="tel:+919910317286" className="block text-slate-600 hover:text-sky-600 transition-colors">
                  +91 9910317286
                </a>
                <a href="tel:+919810153116" className="block text-slate-600 hover:text-sky-600 transition-colors">
                  +91 9810153116
                </a>
              </div>
            </motion.div>

            {/* Email */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="bg-white rounded-xl p-8 shadow-sm"
            >
              <div className="w-12 h-12 rounded-xl bg-sky-50 flex items-center justify-center mb-4">
                <Mail className="w-6 h-6 text-sky-600" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-4">Email</h3>
              <a href="mailto:entdrsachdeva25@gmail.com" className="text-slate-600 hover:text-sky-600 transition-colors">
                entdrsachdeva25@gmail.com
              </a>
            </motion.div>

            {/* WhatsApp */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="bg-white rounded-xl p-8 shadow-sm"
            >
              <div className="w-12 h-12 rounded-xl bg-green-50 flex items-center justify-center mb-4">
                <MessageCircle className="w-6 h-6 text-green-600" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-4">WhatsApp</h3>
              <a 
                href="https://wa.me/919910317286?text=Hello,%20I%20would%20like%20to%20book%20an%20appointment%20with%20Dr.%20Sanjay%20Sachdeva."
                target="_blank"
                rel="noopener noreferrer"
                className="text-slate-600 hover:text-green-600 transition-colors"
              >
                Message us on WhatsApp
              </a>
            </motion.div>
          </div>

          {/* Location Card */}
          <div className="grid lg:grid-cols-2 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-white rounded-xl p-8 shadow-sm"
            >
              <div className="w-12 h-12 rounded-xl bg-sky-50 flex items-center justify-center mb-4">
                <MapPin className="w-6 h-6 text-sky-600" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-4">Clinic Address</h3>
              <p className="text-slate-600 mb-4">
                B-163, Greater Kailash, Part I,<br />
                New Delhi, 110048
              </p>
              <a 
                href="https://maps.google.com/?q=B-163,+Greater+Kailash,+Part+I,+New+Delhi,+110048"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-sky-600 hover:underline"
              >
                Get Directions →
              </a>

              <div className="mt-6 pt-6 border-t border-slate-100">
                <div className="flex items-center gap-2 mb-2">
                  <Clock className="w-5 h-5 text-sky-600" />
                  <span className="font-medium text-slate-900">Clinic Timing</span>
                </div>
                <p className="text-slate-600">
                  Monday to Saturday: 10:00 AM – 7:00 PM<br />
                  Sunday: Closed
                </p>
              </div>
            </motion.div>

            {/* Map Embed */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="bg-white rounded-xl overflow-hidden shadow-sm"
            >
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3504.9233842726893!2d77.22889037549892!3d28.543619389554!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x390ce3cd9cce3c2d%3A0x2a3aa8b5a7b5b5b5!2sGreater%20Kailash%20I%2C%20Greater%20Kailash%2C%20New%20Delhi%2C%20Delhi%20110048!5e0!3m2!1sen!2sin!4v1704067200000!5m2!1sen!2sin"
                width="100%"
                height="400"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Clinic Location"
              />
            </motion.div>
          </div>

          {/* CTA */}
          <div className="mt-12 text-center">
            <h3 className="text-2xl font-bold text-slate-900 mb-4">Ready to Schedule Your Visit?</h3>
            <Link to="/book-appointment">
              <Button size="lg" className="bg-amber-600 hover:bg-amber-700 font-bold">
                Book an Appointment
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Hospital Affiliation */}
      <section className="py-12 bg-white">
        <div className="container-width text-center">
          <h3 className="text-lg font-medium text-slate-500 mb-4">Also Consulting At</h3>
          <div className="inline-flex items-center gap-4 bg-slate-50 px-8 py-4 rounded-xl">
            <div className="w-12 h-12 bg-white rounded-lg flex items-center justify-center shadow-sm">
              <span className="text-xl font-bold text-sky-600">M</span>
            </div>
            <div className="text-left">
              <div className="font-semibold text-slate-900">Max Super Speciality Hospital</div>
              <div className="text-sm text-slate-500">Saket, New Delhi</div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
